/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import React, { useCallback, useEffect, useMemo } from 'react';
import { withTheme } from 'styled-components';
import {
  isFullScreenModeOff,
  registerOnFullscreenChange,
  requestExitFullscreenMode,
  requestFullscreenMode,
} from '../../../utils/FullScreenUtils';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import {
  ALTERNATE_ICON_ACTIVE_CLASS,
  ALTERNATE_ICON_CLASS,
  INITIAL_ICON_ACTIVE_CLASS,
  INITIAL_ICON_CLASS,
} from '../map/StyledControlWrapper';
import StyledProps from '../theme/AppThemeInterface';
import { Tooltip } from '@mui/material';

const FULL_SCREEN_BTN_ID = 'mapFullScreenBtn';

interface FullScreenMapControlProps extends StyledProps {
  map: google.maps.Map;
  mapId: string;

  onEnterFullScreen: Function;
  onExitFullScreen: Function;
}

const FullScreenMapControl = (props: FullScreenMapControlProps) => {
  const { map, mapId, onEnterFullScreen, onExitFullScreen, theme } = props;

  // Used to observe the full screen state of the map. This is used to toggle the fullscreen icon for google maps.
  // Needed to be an observable and not state in the case the user exits fullscreen with the esc character.
  const memoizedToggleFullScreenIconCallback = useCallback(() => {
    const fullScreenBtn = document.getElementById(FULL_SCREEN_BTN_ID);

    if (fullScreenBtn && fullScreenBtn.classList) {
      if (isFullScreenModeOff()) {
        fullScreenBtn.classList.add(INITIAL_ICON_ACTIVE_CLASS);
        fullScreenBtn.classList.remove(ALTERNATE_ICON_ACTIVE_CLASS);

        onExitFullScreen();
      } else {
        fullScreenBtn.classList.add(ALTERNATE_ICON_ACTIVE_CLASS);
        fullScreenBtn.classList.remove(INITIAL_ICON_ACTIVE_CLASS);

        onEnterFullScreen();
      }
    }
  }, [onEnterFullScreen, onExitFullScreen]);

  useEffect(() => {
    const googleMapDiv = document.getElementById(mapId);

    if (googleMapDiv) {
      registerOnFullscreenChange(googleMapDiv, memoizedToggleFullScreenIconCallback);
    }
  }, [memoizedToggleFullScreenIconCallback, mapId]);

  const items = useMemo(() => {
    return [
      {
        render: () => (
          <Tooltip
            arrow
            title="Full Screen"
            placement="left-start"
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: theme.tooltipBackgroundColor },
              },
              arrow: {
                sx: { color: theme.tooltipBackgroundColor },
              },
            }}
          >
            <div>
              <Fullscreen className={INITIAL_ICON_CLASS} />
              <FullscreenExit className={ALTERNATE_ICON_CLASS} />
            </div>
          </Tooltip>
        ),
        buttonTextColor: theme.secondaryButtonTextColor,
        id: FULL_SCREEN_BTN_ID,
        className: INITIAL_ICON_ACTIVE_CLASS,
        onClick: () => {
          const mapDiv = document.getElementById(mapId);

          // toggle fullscreen
          if (mapDiv) {
            if (isFullScreenModeOff()) {
              requestFullscreenMode(mapDiv);
            } else {
              requestExitFullscreenMode();
            }
          }
        },
      },
    ];
  }, [theme, mapId]);

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={32}
      index={MapControlOrderIndex.FULLSCREEN_CONTROL}
    />
  );
};

export default withTheme(FullScreenMapControl);
